<script setup>
import { ref, onMounted } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { toast } from "vue3-toastify";

const router = useRouter();
const route = useRoute();

// Form state
const password = ref('');
const confirmPassword = ref('');
const isSubmitting = ref(false);
const isLoading = ref(true);

// Token and email state
const token = ref('');
const userEmail = ref('');
const isTokenValid = ref(false);

// Password visibility state
const showPassword = ref(false);
const showConfirmPassword = ref(false);

// Verify token on mount
onMounted(async () => {
  // Check if token exists in URL
  token.value = route.query.token;
  
  if (!token.value) {
    toast.error("Invalid or missing reset token", {
      type: 'error',
    });
    router.push('/login');
    return;
  }
  
  // Validate the token
  try {
    const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/auth/verify-reset-token/${token.value}`, {
      method: "GET",
      headers: {
        'Content-Type': 'application/json',
      }
    });
    
    const data = await response.json();
    
    if (response.ok && data.success) {
      isTokenValid.value = true;
      userEmail.value = data.email;
    } else {
      toast.error("Password reset link is invalid or has expired", {
        type: 'error',
      });
      setTimeout(() => {
        router.push('/login');
      }, 3000);
    }
  } catch (error) {
    console.error("Token verification error:", error);
    toast.error("An error occurred while verifying your reset link", {
      type: 'error',
    });
    setTimeout(() => {
      router.push('/login');
    }, 3000);
  } finally {
    isLoading.value = false;
  }
});

// Handle password reset submission
const handleResetPassword = async () => {
  // Password validation
  if (!password.value) {
    toast.error("Password is required", { type: 'error' });
    return;
  }
  
  if (password.value !== confirmPassword.value) {
    toast.error("Passwords do not match", { type: 'error' });
    return;
  }
  
  // Password complexity check (at least 8 chars, 1 uppercase, 1 lowercase, 1 number)
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d).{8,}$/;
  if (!passwordRegex.test(password.value)) {
    toast.error("Password must be at least 8 characters long and contain uppercase, lowercase, and numeric characters", {
      type: 'error',
    });
    return;
  }
  
  isSubmitting.value = true;
  const resetToastId = toast.loading("Resetting your password...", {
    autoClose: false,
    type: 'loading',
    isLoading: true,
  });
  
  try {
    const response = await fetch(`${import.meta.env['PUBLIC_API_URL']}/auth/reset-password`, {
      method: "POST",
      headers: {
        'Content-Type': "application/json",
      },
      body: JSON.stringify({ 
        token: token.value,
        password: password.value
      })
    });
    
    const data = await response.json();
    
    if (response.ok) {
      toast.update(resetToastId, {
        render: "Your password has been updated successfully!",
        autoClose: 5000,
        type: "success",
        isLoading: false,
      });
      
      // Redirect to login page after success
      setTimeout(() => {
        router.push('/login');
      }, 3000);
    } else {
      toast.update(resetToastId, {
        render: data.message || "Password reset failed. Please try again.",
        autoClose: 5000,
        type: "error",
        isLoading: false,
      });
    }
  } catch (error) {
    console.error("Password reset error:", error);
    toast.update(resetToastId, {
      render: "There was an error resetting your password. Please try again later.",
      autoClose: 5000,
      type: "error",
      isLoading: false,
    });
  } finally {
    isSubmitting.value = false;
  }
};

const togglePasswordVisibility = (field) => {
  if (field === 'password') {
    showPassword.value = !showPassword.value;
  } else {
    showConfirmPassword.value = !showConfirmPassword.value;
  }
};
</script>

<style lang="css" scoped>
label:not(.not-required):after {
	content: " *"
}
</style>

<template>
  <div class="w-full h-[100vh] bg-[url('/images/cover.webp')] bg-cover bg-no-repeat p-24 flex flex-col items-end justify-center">
    <div class="border-2 border-white rounded-[20px] w-[500px] min-h-[550px] mr-32 flex flex-col gap-8 justify-center items-center bg-[#19435f] bg-opacity-80">
      <div class="flex flex-col justify-center items-center mt-4">
        <img src="/images/coleman-full.png" width="60%" />
      </div>
      
      <!-- Loading state -->
      <div v-if="isLoading" class="text-white text-center px-8">
        <div class="flex justify-center mb-4">
          <div class="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-white"></div>
        </div>
        <p>Verifying your reset link...</p>
      </div>
      
      <!-- Invalid token state is handled by redirecting to login -->
      
      <!-- Password reset form -->
      <div v-if="!isLoading && isTokenValid" class="w-full px-8">
        <h1 class="text-white text-2xl text-center mb-6">Reset Your Password</h1>
        
        <div class="text-white text-center mb-6">
          <p>Create a new password for</p>
          <p class="font-medium">{{ userEmail }}</p>
        </div>
        
        <form @submit.prevent="handleResetPassword" class="flex flex-col gap-y-5">
          <div class="flex flex-col gap-2">
            <label class="text-white ml-2" for="new-password">New Password</label>
            <div class="relative">
              <input 
                :type="showPassword ? 'text' : 'password'" 
                v-model="password" 
                id="new-password"
                class="w-full border-white border-2 p-2 rounded-[20px] bg-transparent px-4 text-white text-[1rem]" 
              />
              <button 
                type="button"
                @click="togglePasswordVisibility('password')" 
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-white"
              >
                <span v-if="showPassword">🙈</span>
                <span v-else>👁️</span>
              </button>
            </div>
            <p class="text-xs text-gray-300 ml-2">
              Password must be at least 8 characters with uppercase, lowercase, and numeric characters
            </p>
          </div>
          
          <div class="flex flex-col gap-2">
            <label class="text-white ml-2" for="confirm-password">Confirm Password</label>
            <div class="relative">
              <input 
                :type="showConfirmPassword ? 'text' : 'password'" 
                v-model="confirmPassword" 
                id="confirm-password"
                class="w-full border-white border-2 p-2 rounded-[20px] bg-transparent px-4 text-white text-[1rem]" 
              />
              <button 
                type="button"
                @click="togglePasswordVisibility('confirm')" 
                class="absolute right-3 top-1/2 transform -translate-y-1/2 text-white"
              >
                <span v-if="showConfirmPassword" role="button">🙈</span>
                <span v-else role="button">👁️</span>
              </button>
            </div>
          </div>
          
          <div class="flex justify-center mt-4">
            <button 
              type="submit"
              :disabled="isSubmitting"
              class="w-[200px] !cursor-pointer font-monster flex justify-center gap-4 items-center font-semibold bg-white hover:bg-white/60 transition-all ease-in-out duration-[750ms] rounded-[0.625rem] text-secondary p-3 disabled:opacity-70 disabled:cursor-not-allowed"
            >
              <span v-if="isSubmitting">Processing...</span>
              <span v-else>Reset Password</span>
            </button>
          </div>
          
          <div class="text-center mb-4">
            <router-link to="/login" class="text-white hover:underline">
              Back to Login
            </router-link>
          </div>
        </form>
      </div>
    </div>
  </div>
</template>
